<template>
  <div>
    <Head title="Nómina y Turnos (PRO)" />

    <!-- 1. Header del Módulo -->
    <PaysheetHeader
      :title="'Nómina y Destajo'"
      :total-employees="stats?.total_employees || 0"
      @open-bulk-shifts="openBulkShiftModal"
      @open-single-shift="openSingleShiftModal()"
      @open-employee-modal="openEmployeeModal()"
      @open-settlement-modal="openSettlementModal()"
    />

    <!-- 2. KPIs Bento Grid Canónico (4 Tarjetas) -->
    <PaysheetStats :stats="stats" />

    <!-- 3. Navegación por Pestañas -->
    <PaysheetTabs
      :active-tab="currentTab"
      :total-employees="stats?.total_employees || 0"
      :pending-shifts-count="stats?.pending_shifts_count || 0"
      @change-tab="changeTab"
    />

    <!-- 4. Pestaña 1: Dashboard & Estadísticas -->
    <PaysheetDashboardTab
      v-if="currentTab === 'dashboard'"
      :stats="stats"
      :employees-with-pending-shifts="employeesWithPendingShifts"
      :current-exchange-rate="currentExchangeRate"
      @open-settlement-modal="openSettlementModal"
      @change-tab="changeTab"
    />

    <!-- 5. Pestaña 2: Personal (CRUD) -->
    <PaysheetEmployeesTab
      v-else-if="currentTab === 'employees'"
      :employees="employees"
      :locations="locations"
      :events="events"
      :filters="filters"
      :current-exchange-rate="currentExchangeRate"
      @filter="filterEmployees"
      @open-employee-modal="openEmployeeModal"
      @open-single-shift-modal="openSingleShiftModal"
      @open-settlement-modal="openSettlementModal"
      @open-documents-modal="openDocumentsModal"
      @delete-employee="deleteEmployee"
      @copy-to-clipboard="copyToClipboard"
    />

    <!-- 6. Pestaña 3: Turnos & Destajo -->
    <PaysheetShiftsTab
      v-else-if="currentTab === 'shifts'"
      :shifts="shifts"
      :all-active-employees="allActiveEmployees"
      :events="events"
      :filters="filters"
      :current-exchange-rate="currentExchangeRate"
      @filter="filterShifts"
      @reset-filters="resetShiftFilters"
      @open-single-shift-modal="openSingleShiftModal"
      @delete-shift="deleteShift"
    />

    <!-- 7. Pestaña 4: Liquidación y Pagos -->
    <PaysheetPaymentsTab
      v-else-if="currentTab === 'payments'"
      :payments="payments"
      :all-active-employees="allActiveEmployees"
      :filters="filters"
      :current-exchange-rate="currentExchangeRate"
      @filter="filterPayments"
      @open-settlement-modal="openSettlementModal"
      @delete-settlement="deleteSettlement"
    />

    <!-- ==================== MODALES ==================== -->
    <!-- Modal 1: Personal (Crear / Editar) -->
    <EmployeeModal
      :is-open="employeeModalOpen"
      :employee="editingEmployee"
      :events="events"
      :submitting="submitting"
      :current-exchange-rate="currentExchangeRate"
      @close="employeeModalOpen = false"
      @submit="submitEmployee"
    />

    <!-- Modal 2: Registro de Turno Individual -->
    <SingleShiftModal
      :is-open="singleShiftModalOpen"
      :shift="editingShift"
      :target-employee="targetEmployeeForShift"
      :all-active-employees="allActiveEmployees"
      :submitting="submitting"
      :current-exchange-rate="currentExchangeRate"
      @close="singleShiftModalOpen = false"
      @submit="submitSingleShift"
    />

    <!-- Modal 3: Registro Masivo de Turnos -->
    <BulkShiftModal
      :is-open="bulkShiftModalOpen"
      :all-active-employees="allActiveEmployees"
      :submitting="submitting"
      @close="bulkShiftModalOpen = false"
      @submit="submitBulkShifts"
    />

    <!-- Modal 4: Liquidación y Pago -->
    <SettlementModal
      :is-open="settlementModalOpen"
      :target-employee="targetEmployeeForSettlement"
      :all-active-employees="allActiveEmployees"
      :payment-methods="paymentMethods"
      :submitting="submitting"
      :current-exchange-rate="currentExchangeRate"
      @close="settlementModalOpen = false"
      @submit="submitSettlement"
    />

    <!-- Modal 5: Expediente y Documentos -->
    <DocumentsModal
      :is-open="documentsModalOpen"
      :employee="selectedEmployeeForDocs"
      @close="documentsModalOpen = false"
      @document-uploaded="onDocumentUploaded"
      @document-deleted="onDocumentDeleted"
    />
  </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import { Head } from '@inertiajs/inertia-vue3'
import Swal from 'sweetalert2'

import PaysheetHeader from './Partials/PaysheetHeader'
import PaysheetStats from './Partials/PaysheetStats'
import PaysheetTabs from './Partials/PaysheetTabs'
import PaysheetDashboardTab from './Partials/PaysheetDashboardTab'
import PaysheetEmployeesTab from './Partials/PaysheetEmployeesTab'
import PaysheetShiftsTab from './Partials/PaysheetShiftsTab'
import PaysheetPaymentsTab from './Partials/PaysheetPaymentsTab'

import EmployeeModal from './Partials/Modals/EmployeeModal'
import SingleShiftModal from './Partials/Modals/SingleShiftModal'
import BulkShiftModal from './Partials/Modals/BulkShiftModal'
import SettlementModal from './Partials/Modals/SettlementModal'
import DocumentsModal from './Partials/Modals/DocumentsModal'

export default {

  components: {
    Head,
    PaysheetHeader,
    PaysheetStats,
    PaysheetTabs,
    PaysheetDashboardTab,
    PaysheetEmployeesTab,
    PaysheetShiftsTab,
    PaysheetPaymentsTab,
    EmployeeModal,
    SingleShiftModal,
    BulkShiftModal,
    SettlementModal,
    DocumentsModal,
  },
  layout: Layout,

  props: {
    stats: {
      type: Object,
      required: true,
    },
    tab: {
      type: String,
      default: 'dashboard',
    },
    employees: Object,
    allActiveEmployees: {
      type: Array,
      default: () => [],
    },
    shifts: Object,
    payments: Object,
    paymentMethods: {
      type: Array,
      default: () => ['Efectivo Dolar', 'Pago Móvil', 'Transferencia Bs', 'Zelle'],
    },
    currentExchangeRate: {
      type: Number,
      default: 1.0,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    locations: {
      type: Array,
      default: () => [],
    },
    events: {
      type: Array,
      default: () => [],
    },
    isProActive: Boolean,
  },

  data() {
    return {
      currentTab: this.tab || 'dashboard',
      submitting: false,

      // Modal states
      employeeModalOpen: false,
      editingEmployee: null,

      singleShiftModalOpen: false,
      editingShift: null,
      targetEmployeeForShift: null,

      bulkShiftModalOpen: false,

      settlementModalOpen: false,
      targetEmployeeForSettlement: null,

      documentsModalOpen: false,
      selectedEmployeeForDocs: null,
    }
  },

  computed: {
    employeesWithPendingShifts() {
      if (!this.employees?.data) return []
      return this.employees.data.filter(e => (e.pending_shifts_count || 0) > 0)
    },
  },

  watch: {
    tab(newTab) {
      if (newTab) this.currentTab = newTab
    },
  },

  methods: {
    changeTab(tabName) {
      this.currentTab = tabName
      this.$inertia.get(
        '/paysheet',
        { tab: tabName },
        { preserveState: true, preserveScroll: true, replace: true },
      )
    },

    copyToClipboard(text, msg = 'Copiado al portapapeles') {
      if (!navigator.clipboard) return
      navigator.clipboard.writeText(text).then(() => {
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: msg,
          showConfirmButton: false,
          timer: 1500,
        })
      })
    },

    // ----------------- EMPLOYEES CRUD -----------------
    filterEmployees(filters) {
      this.$inertia.get(
        '/paysheet',
        {
          tab: 'employees',
          ...filters,
        },
        { preserveState: true, preserveScroll: true, replace: true },
      )
    },

    openEmployeeModal(emp = null) {
      this.editingEmployee = emp
      this.employeeModalOpen = true
    },

    submitEmployee(formData) {
      this.submitting = true
      if (this.editingEmployee) {
        this.$inertia.put(`/paysheet/employees/${this.editingEmployee.id}`, formData, {
          preserveScroll: true,
          onSuccess: () => {
            this.submitting = false
            this.employeeModalOpen = false
            Swal.fire({
              toast: true,
              position: 'top-end',
              icon: 'success',
              title: 'Personal actualizado correctamente',
              showConfirmButton: false,
              timer: 2000,
            })
          },
          onError: () => {
            this.submitting = false
          },
        })
      } else {
        this.$inertia.post('/paysheet/employees', formData, {
          preserveScroll: true,
          onSuccess: () => {
            this.submitting = false
            this.employeeModalOpen = false
            Swal.fire({
              toast: true,
              position: 'top-end',
              icon: 'success',
              title: 'Personal registrado con éxito',
              showConfirmButton: false,
              timer: 2000,
            })
          },
          onError: () => {
            this.submitting = false
          },
        })
      }
    },

    deleteEmployee(emp) {
      Swal.fire({
        title: `¿Eliminar a ${emp.name}?`,
        text: 'Si el empleado posee turnos o pagos previos, se cambiará a estado Inactivo para preservar el historial contable.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Sí, continuar',
        cancelButtonText: 'Cancelar',
      }).then(res => {
        if (res.isConfirmed) {
          this.$inertia.delete(`/paysheet/employees/${emp.id}`, {
            preserveScroll: true,
            onSuccess: () => {
              Swal.fire({
                icon: 'success',
                title: 'Operación completada',
                showConfirmButton: false,
                timer: 1500,
              })
            },
          })
        }
      })
    },

    // ----------------- SHIFTS CRUD -----------------
    filterShifts(filters) {
      this.$inertia.get(
        '/paysheet',
        {
          tab: 'shifts',
          ...filters,
        },
        { preserveState: true, preserveScroll: true, replace: true },
      )
    },

    resetShiftFilters() {
      this.$inertia.get(
        '/paysheet',
        { tab: 'shifts' },
        { preserveState: true, preserveScroll: true, replace: true },
      )
    },

    openSingleShiftModal(emp = null, shift = null) {
      this.editingShift = shift
      this.targetEmployeeForShift = emp
      this.singleShiftModalOpen = true
    },

    submitSingleShift(formData) {
      this.submitting = true
      if (this.editingShift) {
        this.$inertia.put(`/paysheet/shifts/${this.editingShift.id}`, formData, {
          preserveScroll: true,
          onSuccess: () => {
            this.submitting = false
            this.singleShiftModalOpen = false
            Swal.fire({
              toast: true,
              position: 'top-end',
              icon: 'success',
              title: 'Turno actualizado con éxito',
              showConfirmButton: false,
              timer: 2000,
            })
          },
          onError: () => {
            this.submitting = false
          },
        })
      } else {
        this.$inertia.post('/paysheet/shifts', formData, {
          preserveScroll: true,
          onSuccess: () => {
            this.submitting = false
            this.singleShiftModalOpen = false
            Swal.fire({
              toast: true,
              position: 'top-end',
              icon: 'success',
              title: 'Turno registrado correctamente',
              showConfirmButton: false,
              timer: 2000,
            })
          },
          onError: () => {
            this.submitting = false
          },
        })
      }
    },

    deleteShift(shift) {
      Swal.fire({
        title: '¿Eliminar este turno?',
        text: 'Esta acción no se puede deshacer.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
      }).then(res => {
        if (res.isConfirmed) {
          this.$inertia.delete(`/paysheet/shifts/${shift.id}`, {
            preserveScroll: true,
            onSuccess: () => {
              Swal.fire({
                icon: 'success',
                title: 'Turno eliminado',
                showConfirmButton: false,
                timer: 1500,
              })
            },
          })
        }
      })
    },

    // ----------------- BULK SHIFTS -----------------
    openBulkShiftModal() {
      this.bulkShiftModalOpen = true
    },

    submitBulkShifts(payload) {
      this.submitting = true
      this.$inertia.post(
        '/paysheet/shifts/bulk',
        payload,
        {
          preserveScroll: true,
          onSuccess: () => {
            this.submitting = false
            this.bulkShiftModalOpen = false
            Swal.fire({
              icon: 'success',
              title: 'Turnos registrados',
              text: `Se registraron exitosamente ${payload.shifts.length} turnos.`,
              timer: 2000,
              showConfirmButton: false,
            })
          },
          onError: () => {
            this.submitting = false
          },
        },
      )
    },

    // ----------------- SETTLEMENT & PAYMENTS -----------------
    openSettlementModal(emp = null) {
      this.targetEmployeeForSettlement = emp
      this.settlementModalOpen = true
    },

    submitSettlement(formData) {
      Swal.fire({
        title: '¿Confirmar liquidación y pago?',
        text: `Se liquidarán ${formData.shiftsCount} turnos por $${Number(formData.total).toFixed(2)}. Se creará el gasto respectivo en Compras y Gastos (/shoppings).`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#ea580c',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Sí, liquidar ahora',
        cancelButtonText: 'Cancelar',
      }).then(res => {
        if (res.isConfirmed) {
          this.submitting = true
          this.$inertia.post('/paysheet/settlements', formData, {
            preserveScroll: true,
            onSuccess: () => {
              this.submitting = false
              this.settlementModalOpen = false
              this.currentTab = 'payments'
              Swal.fire({
                icon: 'success',
                title: '¡Liquidación procesada con éxito!',
                text: 'El pago ha sido registrado y el gasto fue reflejado en las finanzas del negocio.',
              })
            },
            onError: err => {
              this.submitting = false
              const msg = err?.shift_ids || 'Ocurrió un error al procesar la liquidación.'
              Swal.fire({
                icon: 'error',
                title: 'Error al liquidar',
                text: msg,
              })
            },
          })
        }
      })
    },

    filterPayments(filters) {
      this.$inertia.get(
        '/paysheet',
        {
          tab: 'payments',
          ...filters,
        },
        { preserveState: true, preserveScroll: true, replace: true },
      )
    },

    deleteSettlement(pay) {
      Swal.fire({
        title: `¿Anular liquidación #${pay.reference_number}?`,
        text: 'Esta acción revertirá todos los turnos incluidos a estado "Pendiente" y ELIMINARÁ el gasto operativo generado en /shoppings.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Sí, anular liquidación',
        cancelButtonText: 'Cancelar',
      }).then(res => {
        if (res.isConfirmed) {
          this.$inertia.delete(`/paysheet/settlements/${pay.id}`, {
            preserveScroll: true,
            onSuccess: () => {
              Swal.fire({
                icon: 'success',
                title: 'Liquidación anulada',
                text: 'Los turnos vuelven a estar pendientes y el gasto fue removido.',
                timer: 2000,
                showConfirmButton: false,
              })
            },
          })
        }
      })
    },

    // ----------------- DOCUMENTS MODAL -----------------
    openDocumentsModal(emp) {
      this.selectedEmployeeForDocs = emp
      this.documentsModalOpen = true
    },

    onDocumentUploaded(emp) {
      if (emp.documents_count !== undefined) {
        emp.documents_count++
      }
    },

    onDocumentDeleted(emp) {
      if (emp.documents_count > 0) {
        emp.documents_count--
      }
    },
  },
}
</script>
